// Optimized Complaints.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    TextField,
    IconButton,
    InputBase,
    Typography,
    MobileStepper,
    FormControl,
    MenuItem,
    Select,
    Dialog
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from '../../../../Utils/constants';
import ServiceDetails from "./serviceDetails";
import useMediaQuery from '@mui/material/useMediaQuery';
import WestIcon from '@mui/icons-material/West';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import Refresh from '@mui/icons-material/Refresh';
import '../ServiceRequest.css';
import { useLocation, useNavigate } from "react-router-dom";
import { getServiceRequestVisits, updateServiceRequestStatus } from "../../../../Services/authService";
import LoadingAnimation from "../../../../Components/Common/LoadingAnimation";
import { DropDownIcon } from '../../../../Components/Layouts/CustomIcons';
import { formatDate } from '../../../../Components/Common/dateFormat';

export default function Complaints() {
    const location = useLocation();
    const navigate = useNavigate();
    const requestId = location.state?.complaintData?.id || location.state?.referenceId;
    
    const [activeStep, setActiveStep] = useState(0);
    const [complaintData, setComplaintData] = useState();
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const { mode } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [isHovered, setIsHovered] = useState(false);
    const videoRef = React.useRef(null);
    const [status, setStatus] = useState(location.state?.complaintData?.status || '');
    const [modalImageUrl, setModalImageUrl] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

    const imgRef = React.useRef(null);
    const isDraggingRef = React.useRef(false);
    const dragStartRef = React.useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const zoomLevelRef = React.useRef(zoomLevel);
    zoomLevelRef.current = zoomLevel;

    // Reset zoom and pan when modal closes
    useEffect(() => {
        if (!modalImageUrl) {
            setZoomLevel(1);
            setPanOffset({ x: 0, y: 0 });
        }
    }, [modalImageUrl]);

    // Document-level mouse event listeners for drag/pan
    useEffect(() => {
        if (!modalImageUrl) return;

        const handleMouseMove = (e) => {
            if (!isDraggingRef.current) return;
            const dx = e.clientX - dragStartRef.current.x;
            const dy = e.clientY - dragStartRef.current.y;
            const newPanX = dragStartRef.current.panX + dx;
            const newPanY = dragStartRef.current.panY + dy;

            const zl = zoomLevelRef.current;
            if (zl <= 1) {
                setPanOffset({ x: 0, y: 0 });
                return;
            }
            const baseW = window.innerWidth * 0.9;
            const baseH = window.innerHeight * 0.9;
            const scaledW = baseW * zl;
            const scaledH = baseH * zl;
            const maxPanX = (scaledW - baseW) / 2;
            const maxPanY = (scaledH - baseH) / 2;
            setPanOffset({
                x: Math.max(-maxPanX, Math.min(maxPanX, newPanX)),
                y: Math.max(-maxPanY, Math.min(maxPanY, newPanY)),
            });
        };

        const handleMouseUp = () => {
            isDraggingRef.current = false;
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [modalImageUrl]);

    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const textColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
    const labeltextColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const deleteColor = mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
    const colorDanger1 = mode === 'dark' ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
    const colorWarning = mode === 'dark' ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
    const colorWarning1 = mode === 'dark' ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
    const colorSuccess = mode === 'dark' ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
    const colorSuccess1 = mode === 'dark' ? DARKCOLORS.Secondary100 : LIGHTCOLORS.Secondary100;
    const borderColor1 = mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
    const dropCircle = mode === 'dark' ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
    const dropIcon = mode === 'dark' ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;

    useEffect(() => {
        if (!complaintData || !complaintData.media?.length || isHovered || modalImageUrl) return;

        const current = complaintData.media[activeStep];
        const isVideo = current?.url?.match(/\.(mp4|mov|avi|webm)$/i);

        if (isVideo) return; // Let video control the flow

        const timer = setTimeout(() => {
            setActiveStep((prev) =>
                prev < complaintData.media.length - 1 ? prev + 1 : 0
            );
        }, 3000); // 3 seconds for images

        return () => clearTimeout(timer);
    }, [activeStep, complaintData, isHovered, modalImageUrl]);

    const getStatusDotStyles = (status) => {
        switch (status) {
            case "Active": case "Completed":
                return {
                    backgroundColor: colorSuccess,
                    border: `3px solid ${colorSuccess1}`,
                };
            case "Inactive": case "In Progress":
                return {
                    backgroundColor: colorWarning,
                    border: `3px solid ${colorWarning1}`,
                };
            case "Expired": case "Open":
                return {
                    backgroundColor: deleteColor,
                    border: `3px solid ${colorDanger1}`,
                };
            default:
                return {
                    backgroundColor: LIGHTCOLORS.Alert500,
                    border: `3px solid ${LIGHTCOLORS.Alert100}`,
                };
        }
    };

    useEffect(() => {
        fetchData();
    }, [requestId]);

    const fetchData = async () => {
        if (!requestId) return;
        try {
            const data = await getServiceRequestVisits(requestId);
            setStatus(data?.status || '');
            setComplaintData(data);
        } catch (error) {
            console.error(t("complaints.fetchError"), error);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => navigate(ROUTES.SERVICE_REQUEST);

    const handleStatusChange = async (e) => {
        const newStatus = e.target.value;
        setStatus(newStatus);
        try {
            await updateServiceRequestStatus(complaintData.id, { status: newStatus });
            fetchData()
        } catch (err) {
            console.error(t('complaints.statusUpdateError'), err);
        }
    }

    const handleMouseDown = (e) => {
        if (zoomLevel <= 1) return;
        e.preventDefault();
        isDraggingRef.current = true;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            panX: panOffset.x,
            panY: panOffset.y,
        };
    };

    if (loading) return <LoadingAnimation />;

    return (
        <Box className="dashboard-container-overview" sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={handleBackClick} sx={{ color: btnColor, backgroundColor: backgroundCard, padding: '0.6rem', borderRadius: '50%', mr: 2 }}>
                    <WestIcon fontSize="small" />
                </IconButton>
                <h2 className="addmachine-text-heading1" style={{ margin: 0, padding: 0 }}>{t("serviceRequest.complaints")}</h2>
            </Box>

            <Box sx={{ backgroundColor: backgroundCard, borderRadius: 3 }}>
                <Box className="complaint-form-container" sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                    <Box
                        className='membership-btn-section'
                        sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: '100%', mb: 2 }}
                    >
                    </Box>
                    <Box className="complaint-form-status" sx={{ display: { xs: 'flex', sm: 'flex', md: 'flex' }, justifyContent: 'space-between', width: '100%' }}>
                        <Box display="flex" alignItems="center" gap="8px" mb="4">
                            <Box
                                sx={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    ...getStatusDotStyles(complaintData?.status),
                                }}
                            ></Box>
                            <Typography variant="h6" className="machine-name-text" gutterBottom>{complaintData?.issueType || complaintData?.issueCategory}</Typography>
                        </Box>
                        <Select
                            value={status}
                            onChange={handleStatusChange}
                            displayEmpty
                            size="small"
                            IconComponent={(props) => (
                                <DropDownIcon color1={dropCircle} color2={dropIcon} {...props} />
                            )}
                            sx={{
                                borderRadius: "24px",
                                border: `0px solid ${borderColor1}`,
                                textAlign: "center",
                                paddingRight: "12px",
                                "& .MuiSelect-select": {
                                    padding: "9px 20px 9px 20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: `0.5px solid ${borderColor1}`,
                                },
                                '@media (max-width: 470px)': {
                                    fontSize: '13px',
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: "16px",
                                        border: `0px solid ${borderColor1}`,
                                        boxShadow: "none",
                                        backgroundColor: backgroundCard,
                                        padding: "10px",
                                        marginTop: "10px",
                                    },
                                },
                            }}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <p className='sort-filter-text'>{t("serviceRequest.status")}</p>;
                                }
                                return t(`serviceRequest.${selected.toLowerCase().replace(/\s/g, '')}`);
                            }}
                        >
                            <MenuItem className='menu-item-sec' value="Open">{t("serviceRequest.open")}</MenuItem>
                            <MenuItem className='menu-item-sec' value="In Progress">{t("serviceRequest.inprogress")}</MenuItem>
                            <MenuItem className='menu-item-sec' value="Completed">{t("serviceRequest.completed")}</MenuItem>
                        </Select>
                    </Box>
                    <Grid container spacing={4}>
                        {/* Complaint Details */}
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.dateReported")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.reportedDate ? formatDate(complaintData.reportedDate) : ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.type")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.franchise ? t("serviceRequest.typePlaceholder") : t("serviceRequest.typePlaceholder1")}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.companyName")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.franchiseName || ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.location")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.location || ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.machine")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.machineNumber || ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.issueCategory")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.issueCategory || ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.issueTypePlaceholder")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                        <InputBase
                                            fullWidth
                                            readOnly
                                            value={complaintData?.issueType || ''}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                },
                                            }} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <p className='label-text-sec' style={{ color: labeltextColor }}>{t("serviceRequest.description")}</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor }}>
                                        <TextField
                                            multiline
                                            fullWidth
                                            readOnly
                                            rows={4}
                                            value={complaintData?.description || ''}
                                            variant="filled"
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: {
                                                    backgroundColor: 'transparent',
                                                    fontSize: '14px',
                                                    color: textColor,
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                    '&::placeholder': {
                                                        color: '#9CA3AF',
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Image Preview */}
                        <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                            {complaintData?.media?.length > 0 ? (
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: isMobile ? "100%" : 720,
                                        padding: '10px',
                                        border: `1px solid ${inputBoxColor} !important`,
                                        mb: 2,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "#fff",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: inputBoxColor,
                                        alignItems: 'center'
                                    }}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                >
                                    <Box sx={{ position: 'relative', width: '100%' }}>
                                        {complaintData.media[activeStep].url.match(/\.(mp4|mov|avi|webm)$/i) ? (
                                            <video
                                                key={activeStep}
                                                ref={videoRef}
                                                controls
                                                autoPlay
                                                style={{
                                                    width: "100%", height: "400px", borderRadius: "6px"
                                                }}
                                            >
                                                <source src={complaintData.media[activeStep]?.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>

                                        ) : (
                                            <img
                                                src={complaintData.media[activeStep].url}
                                                alt={`complaint-media-${activeStep}`}
                                                onClick={() => setModalImageUrl(complaintData.media[activeStep].url)}
                                                style={{
                                                    width: "100%",
                                                    height: "400px",
                                                    objectFit: "cover",
                                                    borderRadius: "6px",
                                                    display: "block",
                                                    cursor: "pointer"
                                                }}
                                            />
                                        )}

                                        {complaintData.media.length > 1 && (
                                            <>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveStep((prev) =>
                                                            prev === 0 ? complaintData.media.length - 1 : prev - 1
                                                        );
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(0,0,0,0.4)',
                                                        color: '#fff',
                                                        opacity: isHovered ? 1 : 0,
                                                        transition: 'opacity 0.3s ease',
                                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                                                        '& .MuiSvgIcon-root': { fontSize: 28 }
                                                    }}
                                                >
                                                    <ChevronLeft />
                                                </IconButton>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveStep((prev) =>
                                                            prev === complaintData.media.length - 1 ? 0 : prev + 1
                                                        );
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(0,0,0,0.4)',
                                                        color: '#fff',
                                                        opacity: isHovered ? 1 : 0,
                                                        transition: 'opacity 0.3s ease',
                                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                                                        '& .MuiSvgIcon-root': { fontSize: 28 }
                                                    }}
                                                >
                                                    <ChevronRight />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>

                                    {complaintData.media.length > 1 && (
                                        <MobileStepper
                                            steps={complaintData.media.length}
                                            position="static"
                                            activeStep={activeStep}
                                            sx={{
                                                bgcolor: "transparent",
                                                width: "100%",
                                                justifyContent: "center",
                                                '.MuiMobileStepper-dots': { justifyContent: 'center', width: '100%' }
                                            }}
                                            nextButton={null}
                                            backButton={null}
                                        />
                                    )}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: isMobile ? "100%" : 720,
                                        padding: '10px',
                                        border: `1px solid ${inputBoxColor} !important`,
                                        mb: 2,
                                        borderRadius: 2,
                                        bgcolor: inputBoxColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 200
                                    }}
                                >
                                    <p style={{ color: textColor, opacity: 0.6, margin: 0 }}>
                                        {t("complaints.noMedia")}
                                    </p>
                                </Box>
                            )}
                        </Grid>
                    </Grid>

                    {/* Optional Section */}
                    <ServiceDetails complaintData={complaintData} />

                    {/* Image Modal */}
                    <Dialog
                        open={Boolean(modalImageUrl)}
                        maxWidth={false}
                        disableEscapeKeyDown
                        PaperProps={{
                            sx: {
                                width: '98vw',
                                height: '98vh',
                                maxWidth: '97vw',
                                maxHeight: '98vh',
                                m: 0,
                                // padding: 10,
                                bgcolor: 'transparent',
                                boxShadow: 'none',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }
                        }}
                    >
                        {/* Close button */}
                        <IconButton
                            onClick={() => setModalImageUrl(null)}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: '#fff',
                                zIndex: 1300,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <Close />
                        </IconButton>

                        {/* Zoom controls */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 24,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                zIndex: 1300,
                                minWidth: '180px',
                            }}
                        >
                            <IconButton
                                onClick={() => setZoomLevel((prev) => Math.max(0.5, +(prev - 0.25).toFixed(2)))}
                                sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                size="small"
                            >
                                <Remove/>
                            </IconButton>
                            <Typography
                                variant="body2"
                                sx={{ color: '#fff', minWidth: 40, textAlign: 'center', userSelect: 'none' }}
                            >
                                {Math.round(zoomLevel * 100)}%
                            </Typography>
                            <IconButton
                                onClick={() => setZoomLevel((prev) => Math.min(3, +(prev + 0.25).toFixed(2)))}
                                sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                size="small"
                            >
                                <Add />
                            </IconButton>
                            <IconButton
                                onClick={() => { setZoomLevel(1); setPanOffset({ x: 0, y: 0 }); }}
                                sx={{
                                    color: '#fff',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    display: (zoomLevel !== 1 || panOffset.x !== 0 || panOffset.y !== 0) ? 'inline-flex' : 'none',
                                }}
                                size="small"
                            >
                                <Refresh />
                            </IconButton>
                        </Box>

                        <img
                            ref={imgRef}
                            src={modalImageUrl}
                            alt="Enlarged complaint media"
                            onMouseDown={handleMouseDown}
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                width: '90vw',
                                objectFit: 'contain',
                                display: 'block',
                                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                                transition: 'transform 0.2s ease',
                                cursor: zoomLevel > 1 ? 'grab' : 'default',
                            }}
                        />
                    </Dialog>
                </Box>
            </Box>
        </Box>
    );
}
